import React, { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { ROUTE_MAP_CONFIG, initializeMapbox } from '@/lib/mapbox-config';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Coordinate {
  latitude: number;
  longitude: number;
}

interface RouteMapProps {
  coordinates: Coordinate[];
  startPoint?: Coordinate;
  endPoint?: Coordinate;
  className?: string;
}

const RouteMapComponent: React.FC<RouteMapProps> = ({ 
  coordinates, 
  startPoint, 
  endPoint,
  className = "h-64 w-full" 
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;
    
    // Initialize Mapbox with token validation
    if (!initializeMapbox()) {
      console.error('Failed to initialize Mapbox. Please check your access token.');
      return;
    }
    
    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: ROUTE_MAP_CONFIG.style,
      center: coordinates.length > 0
        ? [coordinates?.[0]?.longitude, coordinates?.[0]?.latitude]
        : ROUTE_MAP_CONFIG.center,
      zoom: ROUTE_MAP_CONFIG.zoom
    });

    // Wait for map to load before adding route
    map?.current?.on('load', () => {
      if (!map.current || coordinates.length === 0) return;

      // Convert coordinates to GeoJSON format
      const routeGeoJSON = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: Array.isArray(coordinates) ? coordinates.map(coord => [coord.longitude, coord.latitude]) : []
        }
      };

      // Add route source and layer
      map?.current?.addSource('route', {
        type: 'geojson',
        data: routeGeoJSON as any
      });

      // Add the route line
      map?.current?.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3b82f6', // Blue color
          'line-width': 4
        }
      });

      // Add start point marker if provided
      if (startPoint) {
        const startMarkerElement = document.createElement('div');
        startMarkerElement.className = 'start-marker';
        if (startMarkerElement?.style) { startMarkerElement.style.width = '20px'; }
        if (startMarkerElement?.style) { startMarkerElement.style.height = '20px'; }
        if (startMarkerElement?.style) { startMarkerElement.style.borderRadius = '50%'; }
        if (startMarkerElement?.style) { startMarkerElement.style.backgroundColor = '#22c55e'; } // Green
        if (startMarkerElement?.style) { startMarkerElement.style.border = '2px solid white'; }
        if (startMarkerElement?.style) { startMarkerElement.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)'; }

        new mapboxgl.Marker({
          element: startMarkerElement,
          anchor: 'center'
        })
          .setLngLat([startPoint.longitude, startPoint.latitude])
          .addTo(map.current);
      }

      // Add end point marker if provided
      if (endPoint) {
        const endMarkerElement = document.createElement('div');
        endMarkerElement.className = 'end-marker';
        if (endMarkerElement?.style) { endMarkerElement.style.width = '20px'; }
        if (endMarkerElement?.style) { endMarkerElement.style.height = '20px'; }
        if (endMarkerElement?.style) { endMarkerElement.style.borderRadius = '50%'; }
        if (endMarkerElement?.style) { endMarkerElement.style.backgroundColor = '#ef4444'; } // Red
        if (endMarkerElement?.style) { endMarkerElement.style.border = '2px solid white'; }
        if (endMarkerElement?.style) { endMarkerElement.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)'; }

        new mapboxgl.Marker({
          element: endMarkerElement,
          anchor: 'center'
        })
          .setLngLat([endPoint.longitude, endPoint.latitude])
          .addTo(map.current);
      }

      // Fit map to route bounds with padding
      if (coordinates.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        coordinates.forEach(coord => {
          bounds.extend([coord.longitude, coord.latitude]);
        });
        
        map?.current?.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    });

    return () => {
      if (map.current) {
        map?.current?.remove();
        map.current = null;
      }
    };
  }, [coordinates, startPoint, endPoint]);

  return (
    <div ref={mapContainer} className={className} />
  );
};

export default RouteMapComponent;